import { posDb, type CachedProduct, type CachedCustomer } from '@/db/pos-database';
import { encryptManyForStorage } from '@/db/encrypted-access';
import { syncService } from '@/services/sync.service';
import { useAppHealthStore } from '@/stores/use-app-health-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { useLocationStore } from '@/stores/use-location-store';

// ─── Constants ──────────────────────────────────────────────────────────────

const PULL_PAGE_SIZE = 500;
const PERIODIC_SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes

let periodicTimer: ReturnType<typeof setInterval> | null = null;

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Cursor key. Location-scoped entities (PRODUCT) keep one cursor per store —
 * `PRODUCT:<locationId>` — so switching stores starts from epoch for the new
 * location instead of skipping everything synced before the old cursor.
 * Tenant-wide entities (CUSTOMER) keep the bare entity key.
 */
function cursorKey(entityType: string, locationId?: string): string {
  return locationId ? `${entityType}:${locationId}` : entityType;
}

async function getSyncCursor(entityType: string, locationId?: string): Promise<string> {
  const meta = await posDb.sync_metadata.get(cursorKey(entityType, locationId));
  return meta?.last_synced_at ?? '1970-01-01T00:00:00.000Z';
}

async function updateSyncCursor(entityType: string, timestamp: string, locationId?: string): Promise<void> {
  await posDb.sync_metadata.put({ entity_type: cursorKey(entityType, locationId), last_synced_at: timestamp });
}

// ─── Product Sync ───────────────────────────────────────────────────────────

async function pullProducts(): Promise<number> {
  const authState = useAuthStore.getState();
  const tenantId = authState.profile?.tenant_id ?? authState.user?.tenant_id ?? '';
  const locationId = useLocationStore.getState().currentLocationId ?? '';

  if (!tenantId || !locationId) return 0;

  const since = await getSyncCursor('PRODUCT', locationId);
  let totalSynced = 0;
  let page = 1;
  let latestTimestamp = since;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const result = await syncService.pull({
      since,
      entity_type: 'PRODUCT',
      page,
      limit: PULL_PAGE_SIZE,
      location_id: locationId,
    });

    if (result.data.length === 0) break;

    const toDelete: string[] = [];
    const toUpsert: CachedProduct[] = [];

    for (const record of result.data) {
      const payload = record.payload as Record<string, unknown>;

      if (record.operation === 'DELETE') {
        toDelete.push(record.server_id);
      } else {
        toUpsert.push({
          id: record.server_id,
          tenant_id: tenantId,
          // The pull is location-scoped, but a payload without a location (or a
          // server that ignores the scope) must still land in the right store.
          location_id: (payload.location_id as string | undefined) || locationId,
          name: (payload.name as string) ?? '',
          sku: (payload.sku as string) ?? '',
          barcode: (payload.barcode as string | null) ?? null,
          price: (payload.price as number) ?? 0,
          cost: (payload.cost as number) ?? 0,
          gst_rate: (payload.gst_rate as number) ?? 0,
          hsn_code: (payload.hsn_code as string | null) ?? null,
          stock_quantity: (payload.stock_quantity as number) ?? 0,
          category: (payload.category as string | null) ?? null,
          unit: (payload.unit as string) ?? 'PCS',
          is_active: (payload.is_active as boolean) ?? true,
          hide_in_pos: (payload.hide_in_pos as boolean) ?? false,
          hide_in_sales: (payload.hide_in_sales as boolean) ?? false,
          variant_price_min: (payload.variant_price_min as number | null) ?? null,
          variant_price_max: (payload.variant_price_max as number | null) ?? null,
          synced_at: record.synced_at,
        });
      }

      if (record.synced_at > latestTimestamp) {
        latestTimestamp = record.synced_at;
      }
      totalSynced++;
    }

    // Batch writes for performance
    if (toDelete.length > 0) {
      await posDb.products.bulkDelete(toDelete);
    }
    if (toUpsert.length > 0) {
      await posDb.products.bulkPut(
        await encryptManyForStorage(toUpsert, 'products'),
      );
    }

    if (result.data.length < PULL_PAGE_SIZE) break;
    page++;
  }

  if (totalSynced > 0) {
    await updateSyncCursor('PRODUCT', latestTimestamp, locationId);
  }

  return totalSynced;
}

// ─── Customer Sync ──────────────────────────────────────────────────────────

async function pullCustomers(): Promise<number> {
  const since = await getSyncCursor('CUSTOMER');
  let totalSynced = 0;
  let page = 1;
  let latestTimestamp = since;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const result = await syncService.pull({
      since,
      entity_type: 'CUSTOMER',
      page,
      limit: PULL_PAGE_SIZE,
    });

    if (result.data.length === 0) break;

    const toDelete: string[] = [];
    const toUpsert: CachedCustomer[] = [];

    for (const record of result.data) {
      const payload = record.payload as Record<string, unknown>;

      if (record.operation === 'DELETE') {
        toDelete.push(record.server_id);
      } else {
        toUpsert.push({
          id: record.server_id,
          name: (payload.name as string) ?? '',
          phone: (payload.phone as string | null) ?? null,
          email: (payload.email as string | null) ?? null,
          loyalty_points: (payload.loyalty_points as number) ?? 0,
          synced_at: record.synced_at,
          // The pull now serves the customer row itself (sync plan Phase 5), so
          // the till keeps everything an offline view/edit needs.
          date_of_birth: (payload.date_of_birth as string | null) ?? null,
          anniversary: (payload.anniversary as string | null) ?? null,
          address: (payload.address as string | null) ?? null,
          notes: (payload.notes as string | null) ?? null,
          tags: (payload.tags as string[] | undefined) ?? [],
          version: (payload.version as number | undefined) ?? undefined,
        });
      }

      if (record.synced_at > latestTimestamp) {
        latestTimestamp = record.synced_at;
      }
      totalSynced++;
    }

    // Batch writes for performance
    if (toDelete.length > 0) {
      await posDb.customers.bulkDelete(toDelete);
    }
    if (toUpsert.length > 0) {
      await posDb.customers.bulkPut(
        await encryptManyForStorage(toUpsert, 'customers'),
      );
    }

    if (result.data.length < PULL_PAGE_SIZE) break;
    page++;
  }

  if (totalSynced > 0) {
    await updateSyncCursor('CUSTOMER', latestTimestamp);
  }

  return totalSynced;
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Pull all entity types (products + customers) incrementally.
 * Returns total number of records synced.
 */
export async function pullAllUpdates(): Promise<number> {
  if (!navigator.onLine) return 0;

  let total = 0;
  try {
    total += await pullProducts();
    total += await pullCustomers();
    useAppHealthStore.getState().setCatalogSyncError(null);
  } catch (err) {
    useAppHealthStore.getState().setCatalogSyncError(
      err instanceof Error ? err.message : 'Catalog sync failed',
    );
  }
  return total;
}

/**
 * Start periodic catalog sync. Call once at app init.
 */
export function startPeriodicSync(): () => void {
  // Initial sync
  if (navigator.onLine) {
    pullAllUpdates();
  }

  periodicTimer = setInterval(() => {
    if (navigator.onLine) {
      pullAllUpdates();
    }
  }, PERIODIC_SYNC_INTERVAL);

  const handleOnline = () => {
    pullAllUpdates();
  };

  window.addEventListener('online', handleOnline);

  return () => {
    if (periodicTimer) {
      clearInterval(periodicTimer);
      periodicTimer = null;
    }
    window.removeEventListener('online', handleOnline);
  };
}
